import _ from 'lodash'
import { uuid } from 'vue-uuid'
import { store, entityQuery } from '@/main'
import { ftsByDataType, sorterByDataType } from 'CORE/components/data-type/comparators'
import { active, inactive } from 'ENUMS/entity-states-groups'
import Vue from 'vue'

/**
 * @var {ApiMiddleware}
 */
let api = {}

/**
 * Set root path for api
 * @param {ApiMiddleware} middlewareObject
 */
export const setProjectsApiMiddleware = middlewareObject => {
    api = middlewareObject
}

export default () => ({
    state: {
        projects: [],
    },
    getters: {
        projects: state => _.filter(state.projects, { 'isTemplate': false, 'validUntil': null }),
        templates: state => _.filter(state.projects, { 'isTemplate': true, 'validUntil': null }),
        disabledProjects: state => _.filter(state.projects, p => {
            if (p.isTemplate === true) return false
            if (p.validUntil !== null) return true
        }),
        activeProjects: state => {
            return _.filter(state.projects, p => {
                if (p.isTemplate === true) return false
                if (_.get(p, 'validUntil', null) !== null) return false
                try {
                    const entityStateProperty = entityQuery.onePropertyByTag(p, 'entityState')
                    const entityStateId = entityStateProperty.singleValue
                    const state = _.find(_.get(store.getters.currentUser, `company.settings.entity-states.states`, []), ['id', entityStateId])
                    return _.map(active, s => s.id).includes(state.group)
                } catch (e) {
                    console.error(e)
                }
                return true
            })
        },
        inactiveProjects: state => {
            return _.filter(state.projects, p => {
                if (p.isTemplate === true) return false
                if (_.get(p, 'validUntil', null) !== null) return true
                try {
                    const entityStateProperty = entityQuery.onePropertyByTag(p, 'entityState')
                    const entityStateId = entityStateProperty.singleValue
                    const state = _.find(_.get(store.getters.currentUser, `company.settings.entity-states.states`, []), ['id', entityStateId])
                    return _.map(inactive, s => s.id).includes(state.group)
                } catch (e) {
                    console.error(e)
                }
                return false
            })
        },
        getProjectById: state => id => _.find(state.projects, ['id', id]),
        getTemplateById: state => id => _.find(state.projects, ['id', id]),
        projectsByStateId: state => stateId => {
            return _.filter(state.projects, p => {
                if (p.isTemplate === true) return false
                if (_.get(p, 'validUntil', null) !== null) return false
                try {
                    const entityStateProperty = entityQuery.onePropertyByTag(p, 'entityState')
                    const entityStateId = entityStateProperty.singleValue
                    const state = _.find(_.get(store.getters.currentUser, `company.settings.entity-states.states`, []), ['id', entityStateId])
                    return state.group === stateId
                } catch (e) {
                    const isDone = _.get(_.head(entityQuery.propertyValuesByTag(p, 'done')), 'value.value', false)
                    if (stateId === 'active' && isDone === false) return true
                    if (stateId === 'done' && isDone === true) return true
                }
                return false
            })
        },
        projectStates: () => [
            { id: 'active', name: 'Aktivní', icon: 'fa-circle', color: 'GreenYellow' },
            { id: 'done', name: 'Hotové', icon: 'fa-check-square', color: 'ForestGreen' },
        ],
    },
    mutations: {
        setProjects: (state, projects) => {
            state.projects = _
                .chain(projects)
                .map(p => {
                    p.propertyValues = _.mapValues(p.propertyValues, (v, k) => {
                        return {
                            value: v.value,
                            $hidden: _.get(v, '$hidden', false),
                            get $fts() {
                                const dataType = _.get(store.getters.propertyTypeByKey(k), 'type')
                                return ftsByDataType(v, dataType)
                            },
                            get $srt() {
                                const dataType = _.get(store.getters.propertyTypeByKey(k), 'type')
                                return sorterByDataType(v, dataType)
                            },
                        }
                    })
                    return p
                })
                .value()
            return state
        },
        updateProjects: (state, projects) => {
            const p = _
                .chain(state.projects)
                .map(oldProject => {
                    const newProject = _.head(_.filter(projects, p => p.id === oldProject.id))
                    if (_.isPlainObject(newProject)) {
                        return newProject
                    }
                    return oldProject
                })
                .value()
            Vue.set(state, 'projects', p)
            return state
        },
        updateProject: (state, project) => {
            _.each(state.projects, (p, i) => {
                if (p.id === project.id) {
                    Vue.set(p, 'propertyValues', _.get(project, 'propertyValues'))
                    p.propertyValues = _.mapValues(p.propertyValues, (v, k) => {
                        return {
                            value: v.value,
                            get $fts() {
                                const dataType = _.get(store.getters.propertyTypeByKey(k), 'type')
                                return ftsByDataType(v, dataType)
                            },
                            get $srt() {
                                const dataType = _.get(store.getters.propertyTypeByKey(k), 'type')
                                return sorterByDataType(v, dataType)
                            },
                        }
                    })
                }
            })
        },
        addProject: (state, project) => {
            state.projects.push(project)
        },
    },
    actions: {
        async projectsLoad({ commit }) {
            const projects = await api.get('/project')
            commit('setProjects', projects)
        },
        async loadProject({ commit }, id) {
            const data = await api.get(`/project/${id}`)
            commit('updateProject', _.head(data))
            return data
        },
        async updateProject({ commit, rootGetters }, data) {
            const response = await api.put(`/project/${data.id}?mode=remove_missing_properties`, data)
            return response
        },
        async createProject({ commit }, data) {
            const response = await api.post('/project', data)
            console.info(response)
            return response
        },
        async createTemplateFromProject({ rootGetters }, id) {
            const project = _.cloneDeep(rootGetters.getProjectById(id))
            if (project.isTemplate === true) {
                throw 'Not a project'
            }
            const tasks = rootGetters.tasksBy({
                projectId: id,
                taskStatus: '$all',
            })
            project.isTemplate = true
            project.id = uuid.v4()
            await api.post('/project', project)

            _.each(tasks, async t => {
                t.project.id = project.id
                t.id = uuid.v4()
                await api.post('/task', t)
            })

            return project.id
        },
        async createProjectFromTemplate({ rootGetters }, { project, tasks }) {
            // if (project.isTemplate === false) {
            //     throw 'Not a template'
            // }

            project.isTemplate = false
            project.id = uuid.v4()
            await api.post('/project', project)

            _.each(tasks, async t => {
                t.project.id = project.id
                t.id = uuid.v4()
                await api.post('/task', t)
            })

            return project.id
        },
        async disableProject({ rootGetters, dispatch }, id) {
            const projectToDisable = rootGetters.getProjectById(id)
            projectToDisable.validUntil = Math.floor(Date.now() / 1000)
            await dispatch('disableProjectTasks', id)
            await api.put(`/project/${id}`, projectToDisable)
        },
        async fireTrigger({ commit }, [id, triggerId]) {
            return api.post(`/project/${id}/trigger/${triggerId}`, {})
                .then(response => {
                    console.info(response)
                })
        },
    },
})
